import { useQuery, useQueryClient } from "react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/services/functions";
import { useStore } from "@/stores/useStore";
import { firestore } from "@/services/firestore";
import {
  ProfitabilityMetric,
  ProfitabilityBreakdown,
  ProfitChartDataPoint,
  ComputeProfitabilityMetricsRequest,
  ComputeProfitabilityMetricsResponse,
} from "@/types/v2/ProfitabilityMetrics";

/**
 * Enhanced hook for fetching profitability metrics with intelligent caching
 * Integrates with cloud functions and provides real-time cache invalidation
 * Follows the same pattern as useCardStatusMetrics for consistency
 * @param overhead Monthly overhead amount in dollars
 * @param params Optional date range parameters
 * @param params.start Start date for the profitability calculation
 * @param params.end End date for the profitability calculation
 */
export function useProfitabilityMetrics(
  overhead: number,
  params?: { start: Date; end: Date }
) {
  const { shop } = useStore();
  const queryClient = useQueryClient();
  const shopId = shop?.id;
  const lastCacheKeyRef = useRef<string | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const [debouncedOverhead, setDebouncedOverhead] = useState(overhead);

  // Debounce overhead changes to prevent excessive function calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedOverhead(overhead);
    }, 500);

    return () => clearTimeout(timer);
  }, [overhead]);

  const requestConfig: ComputeProfitabilityMetricsRequest = {
    shopId: shopId!,
    overhead: debouncedOverhead,
    dateRangeStart: params?.start?.toISOString(),
    dateRangeEnd: params?.end?.toISOString(),
  };

  // Generate stable query key including overhead and date params
  const queryKey = ["profitabilityMetrics", shopId, requestConfig];

  // Create the callable function outside of query to avoid recreating
  const computeProfitabilityMetricsCallable = useCallback(() => {
    return httpsCallable<
      ComputeProfitabilityMetricsRequest,
      ComputeProfitabilityMetricsResponse
    >(functions, "functionsV2-computeProfitabilityMetrics");
  }, []);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!shopId) throw new Error("Shop ID not available");
      if (debouncedOverhead < 0)
        throw new Error("Overhead must be non-negative");

      const callable = computeProfitabilityMetricsCallable();
      const result = await callable(requestConfig);

      // Return the enhanced response from the cloud function which includes cache metadata
      return result.data;
    },
    enabled: !!shopId && debouncedOverhead >= 0,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60 * 2, // 2 hours - profitability data changes less frequently than card status
    cacheTime: 1000 * 60 * 60 * 24, // 24 hours - keep profitability data in memory longer
    onError: (error) => {
      console.error("Error fetching profitability metrics:", error);
    },
  });

  // Set up real-time cache invalidation listener for profitability metrics
  useEffect(() => {
    if (!shopId || !query.data?.cacheKey) {
      return;
    }

    const currentCacheKey = query.data.cacheKey;

    // Only set up a new subscription if cacheKey actually changed
    if (currentCacheKey === lastCacheKeyRef.current) {
      return;
    }

    // Clean up previous subscription
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    const metricPath = `shops/${shopId}/profitabilityMetrics/${currentCacheKey}`;
    let lastStaleState: boolean | undefined = undefined;

    unsubscribeRef.current = firestore.subscribeDocument<ProfitabilityMetric>(
      metricPath,
      (snapshot) => {
        if (snapshot) {
          const metricData = snapshot as ProfitabilityMetric;
          const currentStaleState = metricData.cache.isStale;

          // Only invalidate if cache became newly stale (transition from false/undefined to true)
          // This prevents loops from age-based staleness that doesn't require invalidation
          if (currentStaleState && lastStaleState === false) {
            // Use setTimeout to avoid immediate refetch during render
            setTimeout(() => {
              queryClient.invalidateQueries(queryKey);
            }, 100);
          }

          lastStaleState = currentStaleState;
        }
      }
    );

    lastCacheKeyRef.current = currentCacheKey;

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [shopId, query.data?.cacheKey, queryClient, queryKey]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  return {
    ...query,
    profitabilityBreakdown: query.data?.metric?.profitabilityBreakdown,
    chartData: query.data?.metric?.chartData,
    dateRange: query.data?.metric?.dateRange,
    overhead: query.data?.metric?.overhead,
    cached: query.data?.cached || false,
    isStale: query.data?.isStale || false,
    backgroundRefresh: query.data?.backgroundRefresh || false,
    computationTimeMs: query.data?.computationTimeMs || 0,
    computedAt: query.data?.metric?.cache?.computedAt,
    cacheKey: query.data?.cacheKey,
  };
}

/**
 * Query factory for consistent query key generation
 * @param shopId
 * @param overhead
 * @param dateRange
 * @param dateRange.start
 * @param dateRange.end
 */
export const profitabilityMetricsQuery = (
  shopId: string,
  overhead: number,
  dateRange?: { start: Date; end: Date }
) => ({
  queryKey: ["profitabilityMetrics", shopId, { shopId, overhead, dateRange }],
  queryFn: async () => {
    const computeProfitabilityMetricsCallable = httpsCallable<
      ComputeProfitabilityMetricsRequest,
      ComputeProfitabilityMetricsResponse
    >(functions, "functionsV2-computeProfitabilityMetrics");

    const result = await computeProfitabilityMetricsCallable({
      shopId,
      overhead,
      dateRangeStart: dateRange?.start?.toISOString(),
      dateRangeEnd: dateRange?.end?.toISOString(),
    });

    return result.data;
  },
  refetchOnWindowFocus: false,
  staleTime: 1000 * 60 * 60 * 2, // 2 hours
  cacheTime: 1000 * 60 * 60 * 24, // 24 hours
});

// Re-export types for convenience
export type {
  ProfitabilityBreakdown,
  ProfitChartDataPoint,
  ComputeProfitabilityMetricsResponse,
};
